import { ReactNode, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import isEqual from 'fast-deep-equal';

import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Title from '@/design_system/Typography/Title';
import { useUpdateRequestClient } from '@/models/request';
import { useClientToken } from '@/services/auth';
import { useBrandOrAppRequestContext } from '@/utils/useBrandOrAppRequestContext';

import ClientForm, {
  ClientFormProps,
  ClientState,
  getClientError,
  useClientState,
} from './ClientForm';

export const ClientFormDrawer = ({
  isOpen,
  setIsOpen,
  customTitle,
  ...props
}: Omit<ClientFormProps, 'state'> & {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  customTitle?: ReactNode;
}) => {
  const clientToken = useClientToken();

  const defaultTitle = clientToken ? (
    <Trans id="client.request.my-info.edit.title">Edit my information</Trans>
  ) : (
    <Trans id="client.request.my-info.edit.title.client">Edit client's information</Trans>
  );

  return (
    <Drawer isOpen={isOpen} onOpenChange={setIsOpen}>
      <DrawerHeader>
        <Title size="200">{customTitle ?? defaultTitle}</Title>
      </DrawerHeader>

      <ClientFormDrawerMain setIsOpen={setIsOpen} {...props} />
    </Drawer>
  );
};

const ClientFormDrawerMain = ({
  setIsOpen,
  ...props
}: Omit<ClientFormProps, 'state'> & {
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const clientState = useClientState();
  const [showErrors, setShowErrors] = useState(false);

  return (
    <>
      <DrawerBody style={{ padding: '1.5rem' }}>
        <ClientForm state={clientState} {...props} showErrors={showErrors} />
      </DrawerBody>
      <ClientFormDrawerFooter
        clientState={clientState}
        setIsOpen={setIsOpen}
        setShowErrors={setShowErrors}
        {...props}
      />
    </>
  );
};

const ClientFormDrawerFooter = ({
  clientState,
  setIsOpen,
  setShowErrors,
  ...props
}: Omit<ClientFormProps, 'state'> & {
  clientState: ClientState;
  setIsOpen: (isOpen: boolean) => void;
  setShowErrors: (showErrors: boolean) => void;
}) => {
  const { t } = useLingui();
  const { request, view } = useBrandOrAppRequestContext();
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: updateRequestClient, isPending: isUpdateRequestClientPending } =
    useUpdateRequestClient(request.id);

  // To compare the previous and current client data, we need to normalize them first
  const normalizeClient = (
    client: Partial<typeof request.client> | typeof clientState,
    vip = false
  ) => {
    // To compare the addresses, we need to remove the id field from the objects
    const normalizeAddress = (address: Record<string, unknown> | null | undefined) => {
      if (!address) return null;

      return Object.fromEntries(Object.entries(address).filter(([key]) => key !== 'id'));
    };

    return {
      name: client?.name ?? '',
      title: client?.title ?? null,
      firstName: client?.firstName ?? '',
      lastName: client?.lastName ?? '',
      email: client?.email ?? '',
      address: normalizeAddress(client?.address),
      billingAddress: normalizeAddress(client?.billingAddress),
      phone: client?.phone ?? null,
      deliveryOption: client?.deliveryOption ?? 'store',
      sendcloudServicePointId: client?.sendcloudServicePointId ?? null,
      locale: client?.locale ?? 'fr',
      vip,
    };
  };

  const hasClientChanged = () => {
    const current = normalizeClient(clientState, clientState.vip);
    const initial = normalizeClient(request.client, request.vip);

    const changed = !isEqual(current, initial);

    return changed;
  };

  const saveClient = async () => {
    setShowErrors(true);
    setApiError(null);

    if (
      getClientError({
        client: clientState,
        view: view.client,
      }).hasError
    ) {
      return;
    }

    // Close drawer without API call if nothing changed
    if (!hasClientChanged()) {
      setIsOpen(false);

      return;
    }

    try {
      await updateRequestClient({
        client: {
          name: clientState.name,
          title: clientState.title,
          firstName: clientState.firstName,
          lastName: clientState.lastName,
          email: clientState.email,
          address: clientState.address ?? undefined,
          billingAddress: view.client.billingAddress.enabled
            ? (clientState.billingAddress ?? undefined)
            : undefined,
          deliveryOption: props.hideDeliveryOption ? undefined : clientState.deliveryOption,
          sendcloudServicePointId: clientState.sendcloudServicePointId,
          phone: clientState.phone,
          locale: props.hideLanguage ? undefined : clientState.locale,
        },
        vip: view.client.vip.enabled && !props.hideVip ? clientState.vip : undefined,
      });

      setIsOpen(false);
    } catch (err: any) {
      console.error(err);

      if ((err.message as string).includes('The selected country is not supported')) {
        setApiError(
          t({
            id: 'client.new.checkout.send.country-error',
            message: 'The selected country is not supported',
          })
        );
      } else {
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }
    }
  };

  return (
    <Footer>
      {!!apiError && <FooterError>{apiError}</FooterError>}
      <FooterButtons>
        <Button
          variant={props.variant === 'brand' ? 'secondary-brand' : 'secondary'}
          size={props.size}
          onPress={() => setIsOpen(false)}
        >
          <Trans id="client.request.my-info.edit.cancel">Cancel</Trans>
        </Button>
        <Button
          variant={props.variant === 'brand' ? 'brand' : 'primary'}
          size={props.size}
          onPress={saveClient}
          isLoading={isUpdateRequestClientPending}
        >
          <Trans id="client.request.my-info.edit.save">Save</Trans>
        </Button>
      </FooterButtons>
    </Footer>
  );
};
